<template>
  <div class="sidecart-wrapper">
    <transition name="fade">
      <div class="sidecart__overlay" v-if="sidecartOpen" @click="$store.dispatch('cart/closeSidecart')"></div>
    </transition>
    <transition name="off-canvas-right">
      <div class="sidecart" v-show="sidecartOpen" v-scroll-lock="sidecartOpen && isNotMobile">
        <div class="sidecart__header" :class="{'sidecart__header--border-bottom': !shippingPromoEnabled}">
          <h6 class="sidecart__title">Your Bag {{cartCount(totalItems)}}</h6>
          <button class="sidecart__close" label="Close side cart" @click="$store.dispatch('cart/closeSidecart')" aria-label="Close side cart" ref="sidebarCloseButton">
            <icon name="close" size="19px"></icon>
          </button>
        </div>
        <div class="sidecart__shipping-promo" v-if="shippingPromoEnabled">
          <transition name="fade">
            <span class="sidecart__shipping-promo-message body-md" v-if="normalizedTotalPrice &lt; shippingPromoTotal" :class="colors.shippingText">
              {{`You are $${remainingThresholdValue} away from free shipping.`}}
            </span>
            <span class="sidecart__shipping-promo-message body-md" v-if="normalizedTotalPrice >= shippingPromoTotal" :class="colors.shippingText">
              {{shippingPromoCopy}}
            </span>
          </transition>
        </div>
        <div class="sidecart__shipping-promo-progress-bar-wrapper">
          <div class="sidecart__shipping-promo-progress-bar" v-if="shippingPromoEnabled" :style="{width: `${remainingThresholdPercentage}%`}" :class="colors.shippingProgress"></div>
        </div>
        <div class="sidecart__container">
          <transition name="fade" mode="out-in">
            <div class="sidecart__contents-wrapper" v-if="totalItems > 0" key="content">
              <div class="sidecart__contents" id="sidecartContents">
                <sidecart-line-item v-for="(item, index) in itemsNotInKits" :item="item" :key="index" :isInlineCart="true"/>

                <div class="sidecart__upsell" v-if="false">
                  <h3 class="sidecart__upsell-title">{{ upsellTitle }}</h3>
                  <div class="sidecart__upsell-items">
                    <div class="upsell-item"
                         v-for="upsell in recommendedProducts"
                         :key="upsell.id"
                         :ref="'upsell-' + upsell.variants[0].id">
                      <a :href="upsell.url"
                         class="upsell-item__link">
                        <h4 class="upsell-item__title">{{ upsell.vendor }}</h4>
                        <img :src="imageSize(findImage(upsell), 'x180')"
                             class="upsell-item__image"
                             :alt="upsell.title" />
                        <p class="upsell-item__product">
                          {{ upsell.title }}
                        </p>
                        <p class="upsell-item__price">{{ upsell.variants[0].price | moneyWithoutDecimals }}</p>
                      </a>
                      <v-button secondary class="upsell-item__cta" label="Add To Cart" :promise="addToCartPromise" @click.stop="addToCart(upsell)"></v-button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="sidecart__footer" :class="enableFooterDropshadow ? 'footer-dropshadow-enabled' : ''">
                <table class="sidecart__table">
                  <tbody class="sidecart__table-body">
                    <tr class="sidecart__table-row sidecart__table-row--discount color-util-sale" v-if="savingsTotal && savingsTotal > 0">
                      <td class="sidecart__table-data-type savings body-sm">Discount</td>
                      <td class="sidecart__table-data-price savings body-sm">-{{ savingsTotal | moneyWithoutDecimals }}</td>
                    </tr>
                    <tr class="sidecart__table-row sidecart__table-row--subtotal">
                      <td class="sidecart__table-data-type body-md subtotal">Subtotal</td>
                      <td class="sidecart__table-data-price body-md subtotal">{{ totalPrice | money }}</td>
                    </tr>
                    <tr class="sidecart__table-row sidecart__table-row--footnote">
                      <td class="sidecart__table-data-footnote body-sm">*Shipping Calculated at Checkout</td>
                    </tr>
                  </tbody>
                </table>
                <v-button class="h7 sidecart__checkout-cta ab_test_cta" :full="true" type="submit" name="checkout" value="Checkout" @click="goToCheckout" :class="[{'sidecart__checkout-cta-promo-active' : promoMessage }, colors.cta]" label="Checkout" ></v-button>
                <div class="sidecart__promo" v-if="promoMessage">
                  <p class="sidecart__promo__copy body-sm">{{ promoMessage }}</p>
                </div>
                <div class="sidecart__afterpay">
                  <afterpay-placement data-locale="en_US" data-currency="USD" :data-amount="totalPrice | moneyWithoutDecimals" data-logo-type="lockup" data-modal-link-style="circled-question-icon" data-modal-theme="white" data-size="xs"></afterpay-placement>
                </div>
              </div>
            </div>
            <div class="sidecart__empty" v-else key="empty">
              <p class="sidecart__empty__message">{{ emptyText }}</p>
              <div class="sidecart__empty__link-container">
                <v-link
                  class="sidecart__empty__link link link--primary"
                  v-for="(link, index) in emptyCartLinks"
                  :href="link.url"
                  :label="link.text"
                  :key="index"
                  tag="a"
                >
                  {{ link.text }}
                </v-link>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import Icon from '@/scripts/components/basic/Icon.vue'
import VButton from '@/scripts/components/buttons/VButton.vue'
import { moneyWithoutDecimals } from '@/scripts/filters/money.js'
import VLink from '@/scripts/components/buttons/VLink.vue'
import AsyncButton from '@/scripts/components/buttons/AsyncButton.vue'
import TextAreaInput from '@/scripts/components/forms/TextAreaInput.vue'
import SidecartLineItem from './SidecartLineItem.vue'
import axios from 'axios'
import { imageSize } from '@/scripts/helpers/util.js'


export default {
  name: 'Sidecart',
  components: {
    VButton,
    SidecartLineItem,
    Icon,
    VLink,
    TextAreaInput,
    AsyncButton
  },
  props: {
    cartObject: Object,
    promoMessage: String,
    emptyText: String,
    emptyLinkArray: Array,
    shippingPromoEnabled: Boolean,
    shippingPromoTotal: Number,
    shippingPromoCopy: String,
    upsellTitle: String,
    colors: {
      type: Object,
      default: () => window.bvaccel.cart.colors
    }
  },
  data () {
    return {
      giftEnabled: false,
      giftMessageEnabled: false,
      giftMessage: null,
      giftMessageSuccess: null,
      enableFooterDropshadow: false,
      giftMessageConfirmationActive: false,
      giftMessageConfirmationMessage: '',
      recommended: [],
      addToCartPromise: null,
    }
  },
  computed: {
    ...mapState({
      allProducts: state => state.products.all,
      sidecartOpen: state => state.cart.sidecartOpen,
      items: state => state.cart.checkout.items,
      itemsSubtotal: state => state.cart.checkout.items_subtotal_price,
      totalDiscount: state => state.cart.checkout.total_discount,
      totalPrice: state => state.cart.checkout.total_price,
      totalItems: state => state.cart.checkout.item_count,
      kits: state => state.cart.kits,
      cartIcon: state =>
        state.header.settings &&
          (state.header.settings.cartIconSvgUrl ||
            state.header.settings.cartIconImage ||
            state.header.defaultIcons.cartIconDefault),
      cartIconAlt: state => state.header.settings.cartIconImageAlt
    }),
    isNotMobile () {
      if (this.$mq !== 'mobile') {
        return true
      } return false
    },
    normalizedTotalPrice () {
      return parseFloat(this.totalPrice / 100)
    },
    remainingThresholdValue () {
      if (this.normalizedTotalPrice < this.shippingPromoTotal) {
        return parseFloat((this.shippingPromoTotal - this.normalizedTotalPrice)).toFixed(0)
      }
      return 0
    },
    remainingThresholdPercentage () {
      if (this.normalizedTotalPrice < this.shippingPromoTotal) {
        return parseInt((this.normalizedTotalPrice / this.shippingPromoTotal * 100).toFixed(0))
      }
      return 100
    },
    savingsTotal () {
      let totalDiscounts = 0;
      let fullPrice, discountPrice;

      this.items && this.items.forEach(item => {
        this.allProducts.forEach(product => {
          // calculate discounts for each product variant
          product.variants.forEach(variant => {
            if (item.id === variant.id && variant.compare_at_price > variant.price) {
              fullPrice = variant.compare_at_price * item.quantity;
              discountPrice = variant.price * item.quantity;
              totalDiscounts += fullPrice - discountPrice;
            }
          });
        });
      });
      return totalDiscounts;
    },
    emptyCartLinks () {
      return this.emptyLinkArray.filter(link => link.text);
    },
    itemsNotInKits () {
      return this.items.filter(item => !item.properties || !item.properties._kitId)
    },
    recommendedProducts () {
      const IDsInCart = this.items.map((item) => item.product_id);
      const recommendedNotInCart = this.recommended.filter((item) => !IDsInCart.includes(item.id));
      return recommendedNotInCart.slice(0, 4);
    },
  },
  watch: {
    sidecartOpen () {
      this.updateDropShadow()
      if (this.sidecartOpen) {
        setTimeout(() => this.$refs.sidebarCloseButton.focus(), 100);
      }
      this.fetchNewUpsells();
      //fixing the bug
      if (this.sidecartOpen) {
        document.documentElement.style.overflow = 'hidden'
      } else {
        document.documentElement.style.overflow = 'auto'
      }
    },
    totalItems () {
      this.updateDropShadow()
      // this.addFreeGift()
    }
  },
  mounted () {
    if (this.thresholdProductHandle) {
      this.$store.dispatch('products/addProduct', this.thresholdProductHandle)
    }
    this.updateGiftMessage()
    this.initBoldUpsell()
    this.fetchNewUpsells()
    this.injectAfterPayScript()
  },
  updated () {
    this.initBoldUpsell()
  },
  methods: {
    imageSize,
    async fetchNewUpsells () {
      if (!this.items || this.items.length === 0) return;

      const toFetchFor = this.items[0];
      const req = await fetch(`/recommendations/products.json?product_id=${toFetchFor.product_id}`, {
        headers: {
          'Content-Type': 'application/json'
        },
      });
      const res = await req.json();
      const models = await Promise.all(
        res.products.map(async (item) => {
          try {
            const model = await fetch(`/products/${item.handle}?view=json`);
            return await model.json();
          } catch (e) {
            console.error(e);
            console.warn(`failed to fetch item model: ${item.handle}`);
            return {};
          }
        }),
      );

      this.recommended = models;
    },
    initBoldUpsell () {
      // init bold
      if (window.BOLD && BOLD.common && BOLD.common.eventEmitter && typeof BOLD.common.eventEmitter.emit === 'function') {
        BOLD.common.eventEmitter.emit('BOLD_COMMON_cart_loaded')
      }
    },
    addFreeGift () {
      //Todo: Get Subtotal from cart
      console.log('addFreeGift', this.totalPrice);
      //Todo: find the product in cart for this key 41064296743104 (Dassai Mask variant in the Test)
      //Todo: find the product in cart for this key 39643127513251 (Dassai Mask variant in the Production)
      console.log('addFreeGift Items', this.items);
      console.log('addFreeGift Items', this.items.filter(item => item.id === 39643127513251).length);
      console.log('addFreeGift Total Price', this.totalPrice);
      if (this.totalPrice >= 10000 && this.items.filter(item => item.id === 39643127513251).length === 0) {
        this.addToCartPromise = this.$store.dispatch('cart/addItems', {
          id: 39643127513251,
          quantity: 1,
        })
        .then(() => {
          this.$store.dispatch('cart/afterAddToCart')
          //this.$store.dispatch('toast/send', { text: 'Free Item added to cart', type: 'error' })
        })
        .catch((error) =>
         this.$store.dispatch('toast/send', { text: error, type: 'error' })
         )
      }

    },
    toggleGift () {
      this.giftEnabled = !this.giftEnabled
      this.updateGiftMessage()
    },
    addRemoveGiftMessage () {
      this.giftMessageEnabled = !this.giftMessageEnabled
      if (this.giftMessageE) { this.updateGiftMessage() }
    },
    checkSpecialChars (message) {
      const pattern = RegExp(/[^\u0020-\u007E]/gm)
      return pattern.test(message)
    },
    injectAfterPayScript () {
      let script = document.createElement('script');
      script.setAttribute('src','//js.afterpay.com/afterpay-1.x.js');
      document.head.appendChild(script);
    },
    updateGiftMessage () {
      if (this.giftEnabled === true && this.giftMessage !== '') {
        if (this.checkSpecialChars(this.giftMessage)) {
          this.giftMessageConfirmationMessage = 'Error! Please remove special characters and line breaks.'
          this.giftMessageConfirmation()
        } else {
          this.giftMessageConfirmationMessage = 'Message Saved!'
          this.giftMessageSuccess = axios.post('/cart/update.js', {
            attributes: {
              giftEnabled: this.giftEnabled,
              giftMessage: this.giftMessage
            }
          })
        }
      }
      if (this.giftEnabled === true && this.giftMessage === '') {
        this.giftMessageSuccess = axios.post('/cart/update.js', {
          attributes: {
            giftEnabled: this.giftEnabled
          }
        })
      }
      if (this.giftEnabled === false) {
        this.giftMessageSuccess = axios.post('/cart/update.js', {
          attributes: {
            giftEnabled: this.giftEnabled
          }
        })
      }
    },
    debouncedUpdateGiftMessage () {
      this.giftMessageConfirmationActive = false
      clearTimeout(this.timeout)
      const self = this
      this.timeout = setTimeout(function () {
        self.updateGiftMessage()
        document.getElementById('giftMessageInput').blur()
        self.giftMessageConfirmation()
      }, 3000)
    },
    giftMessageConfirmation () {
      this.giftMessageConfirmationActive = true
    },
    goToCheckout () {
      window.location = '/checkout'
    },
    updateDropShadow () {
      if (document.getElementById('sidecartContents')) {
        setTimeout(() => {
          const contentsElement = document.getElementById('sidecartContents')
          const scroll = contentsElement.scrollHeight
          const offset = contentsElement.offsetHeight
          if (scroll > offset) {
            this.enableFooterDropshadow = true
            this.$forceUpdate()
          } else {
            this.enableFooterDropshadow = false
            this.$forceUpdate()
          }
        }, 300)
      }
    },
    cartCount (totalItems) {
      if (totalItems < 100 && totalItems !== 0) {
        return `(${totalItems})`
      } else if (totalItems > 99) {
        return '(99+)'
      } else if (totalItems === 0) {
        return
      }
    },
    /**
     * @desc add the product to the vuex module, then add its first variant to the cart.
     */
    addToCart (productModel) {
      const variant = productModel.variants[0];
      const lineItemEl = this.$refs[`upsell-${variant.id}`][0];
      lineItemEl.classList.add('hidden');
      this.$store.commit('products/ADD_PRODUCT', productModel);

      setTimeout(() => {
        let items = [{ id: variant.id, quantity: 1 }];
        this.addToCartPromise = this.$store.dispatch('cart/addItems', items)
          .then(() => window.setTimeout(() => this.$store.dispatch('cart/afterAddToCart'), 1000))
          .catch(error => this.$store.dispatch('toast/send', { text: error, type: 'error' }))
        return this.addToCartPromise
      }, 1000);
    },
    /**
     * @desc Prefer the variant image if available, otherwise display Product's image.
     */
    findImage(upsell) {
      if (!upsell.variants[0].image.id) {
        return upsell.images[0].src;
      }

      return upsell.variants[0].image.src;
    },
  }
}
</script>

<style scoped lang="scss">
  .sidecart {
    display: flex;
    flex-direction: column;
    background-color: $color-white;

    @include tablet-up {
      max-width: 450px;
    }

    &__table-row {
      &--discount {
        margin-bottom: 15px;
      }
      &--subtotal {
        margin-bottom: 12px;
      }
      &--footnote {
        margin-bottom: 10px;
      }
    }

    &, &__overlay {
      position: fixed;
      right: 0;
      top: 0;
      height: 100%;
      width: 100%;
    }

    &__container {
      display: flex;
      flex-grow: 1;
      min-height: 0;  // override auto to prevent growing too tall
      height: 100%;
    }

    &__contents {
      flex-grow: 1;
      padding: 0;
      overflow-y: auto;
      overflow-x: hidden;

      &::-webkit-scrollbar {
        width: 0;
      }
      &::-webkit-scrollbar-track,
      &::-webkit-scrollbar-thumb {
        background-color: transparent;
      }

      &-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        flex-grow: 1;
        height: calc(100vh - 70px); // declared height for IE 11
        height: -webkit-fill-available; // adjusts for bottom bar on iOS - will fallback safely if not supported
      }
    }

    &__overlay {
      background-color: $color-black;
      opacity: 0.6;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border: none;
      position: relative;
      margin-bottom: 20px;
      padding: 30px 15px 0 15px !important;
    }

    &__header--border-bottom {
      border-bottom: 1px solid $color-grey;
    }

    &__close {
      opacity: 1;
      transition: opacity 200ms ease;
      &:hover {
        opacity: 0.5;
      }
    }
    &__icon-total-container {
      position: relative;
    }
    &__shipping-promo {
      background-color: $color-white-tea-400;
      text-align: center;
      padding: 18px 20px;
      @include tablet-up {
        padding: 18px 20px;
      }
    }
    &__shipping-promo-progress-bar-wrapper {
      background-color: $color-green-slate-100;
      width: 100%;
      height: 8px;
    }
    &__shipping-promo-progress-bar {
      background-color: $color-green-slate-200;
      height: 8px;
      transition: width 200ms ease;
      margin-bottom: 50px;
    }
    &__footer {
      background-color: $color-white-tea-300;
      border: none;
      position: relative;
      &.footer-dropshadow-enabled {
        z-index: 1;
        box-shadow: 0 -1px 8px rgba(0,0,0,0.1);
      }
    }
    &__gift-messaging {
      padding: 16px 0;
      border-top: 1px solid $color-grey;
      border-bottom: 1px solid $color-grey;
      margin: 8px 0 16px;
      &-header {
        display: flex;
        justify-content: space-between;
      }
      &-edit {
        cursor: pointer;
      }
      &-message {
        display: flex;
        flex-direction: column;
        &-confirmation {
          color: $color-util-success;
          margin-top: 8px;
        }
      }
      &-textarea {
        margin-top: 18px;
        margin-bottom: 0;
      }
      &-submit {
        align-self: flex-end;
      }
    }
    &__table {
      display: table;
      border-collapse: collapse;
      width: 100%;
      &-body {
        width: 100%;
      }
      &-row {
        display: table;
        width: 100%;
      }
      &-data-price {
        text-align: right;
      }
      &-data-footnote {
        color: $color-grey;
      }
    }
    &__promo {
      background-color: $color-brand-tertiary;
      padding: 16px;
      &__copy {
        margin: 0;
        padding: 0;
        text-align: center;
      }
    }
    &__empty {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      text-align: center;
      width: 100%;
      max-width: 250px;
      padding: 80px 20px;
      margin: 0 auto;

      &__message {
        @include h6-desktop;
      }
      &__image,
      &__message {
        margin: 0 0 24px;
      }
      &__link-container {
        display: flex;
        align-items: center;
        flex-direction: column;
      }
      /deep/ .sidecart__empty__link {
        margin-right: 15px;
        margin-bottom: 15px;
        margin-right: 0;
      }
    }
    &__checkout-cta {
      margin-top: 0px;
      height: 44px;
      display: flex;

      /deep/ .button {
        color: $color-white;
        letter-spacing: 0;
        line-height: 20px;
      }
    }
    &__checkout-cta-promo-active {
      margin-bottom: 16px;
    }
    &__header,
    &__footer {
      padding: 25px;
      flex-shrink: 0;
    }
    &__footer {
      padding-top: 15px;
      padding-bottom: 0px;
    }
    &__empty__link {
      padding: 0;
    }
    &__cart-image {
      width: 30px;
      height: auto;
    }
  }

  .sidecart__checkout-cta {
    min-height: 44px;
  }
 
  
  .sidecart__checkout-cta.button {
    background: $color-black-900;
      height: 44px;
      &:hover {
        background-color: $color-white;
        border: 2px solid #1D1D20;
        /deep/ .button__content {
          color: #1D1D20;
        }
      }
    }

  .sidecart__checkout-cta.link--secondary {
    &:hover {
      &:before {
        height: 100%;
      }
    }
  }

  .sidecart__title {
    @include h4;
  }

  .sidecart__container,
  .sidecart__contents-wrapper {
    width: 100%;
  }

  /deep/ .sidecart__table-row--subtotal {
    .sidecart__table-data-type,
    .sidecart__table-data-price {
      font-family: $font-family-primary;
      font-size: 1.2rem;
    }
  }

  /deep/ .sidecart__line-item__title,
  /deep/ .sidecart__line-item__options span,
  /deep/ .sidecart__line-item__quantity__label,
  .sidecart__checkout-cta .button__content {
    font-family: $font-family-secondary !important;
  }

  .sidecart__upsell-items {
    padding-left: 20px;
    margin-bottom: 16px;
    max-width: 450px;
    display: flex;
    overflow-x: scroll;

    &::after {
      content: '';
      display: block;
      width: 20px;
      flex-shrink: 0;
    }

    @include desktop-up {
      &::-webkit-scrollbar {
        -webkit-appearance: none;
        height: 7px;
      }

      &::-webkit-scrollbar-track {
        margin: 0 25px;
      }

      &::-webkit-scrollbar-thumb {
        border-radius: 4px;
        background-color: rgba(0, 0, 0, 0);
        box-shadow: 0 0 1px rgba(255, 255, 255, .5);
        transition: background-color .2s;
      }

      &:hover::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.5);
      }
    }
  }

  .upsell-item {
    margin: 0 5px 5px;
    padding: 12px 14px 11px;
    min-width: 155px;
    display: flex;
    flex-direction: column;
    background-color: $collection-filter-bg;
    opacity: 1;
    transition: opacity .5s, margin .5s ease .5s, width .5s ease .5s, min-width .5s ease .5s, padding .5s ease .5s;

    &.hidden {
      margin: 0;
      padding: 0;
      width: 0;
      min-width: 0;
      opacity: 0;
    }
  }

  .sidecart__upsell-title {
    margin-bottom: 10px;
    padding-left: 26px;
    font-size: rem(15);
    letter-spacing: 0;
    line-height: rem(22);
    text-transform: none;
    color: $color-black-900;
  }

  .upsell-item__title {
    text-align: center;
    font-size: rem(11);
    line-height: 1;
    color: $color-black-600;
    font-family: $font-family-secondary;
    text-transform: uppercase;
  }

  .upsell-item__product {
    margin-top: 4px;
    text-align: center;
    font-size: rem(11);
    letter-spacing: 0;
    line-height: rem(16);
    font-family: $font-family-secondary;
    font-weight: 300;
  }

  .upsell-item__price {
    margin-top: 4px;
    margin-bottom: 10px;
    text-align: center;
    font-size: rem(13);
    letter-spacing: 0;
    line-height: rem(15);
    font-weight: 300;
  }

  .upsell-item__image {
    margin-bottom: auto;
    max-height: 120px;
    width: 100%;
    object-fit: cover;
  }

  .upsell-item__cta {
    min-width: 0px;
    width: 100%;

    /deep/ .button__content {
      padding: 0 8px;
      font-size: rem(14);
    }

    /deep/ .button,
    /deep/ .button__content {
      width: 100%;
      min-width: 0;
    }
  }

  .upsell-item__product {
    color: $color-black;
    text-decoration: none;
  }

  .upsell-item__link {
    display: block;
    flex-grow: 1;
    text-decoration: none;
    color: inherit;
  }
  .sidecart__afterpay {
    text-align:center;
  }
</style>
